import moment from "moment";
import React, { FC, useEffect, useMemo } from "react";
import { LuChevronRight, LuMail, LuPaperclip, LuPlus } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import { Card } from "../../../../components/card/Card";
import { LottieLoading } from "../../../../components/graphics/LottieLoading";
import { Avatar } from "../../../../components/newUI/avatar";
import { Button } from "../../../../components/newUI/button";
import { Email } from "../../../../factories/emails/models/emails.models";
import { useAppSelector } from "../../../../redux/redux";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  useGetEmailFoldersQuery,
  useGetRedirectUrlMutation,
  useLazyGetContactEmailsQuery,
} from "../../../../slices/EmailsSlice";
import { ProfileWidgetEmptyState } from "./ProfileWidgetEmptyState";

type Props = {
  contactEmail?: string;
  route: string;
};

const getSenderName = (email: Email) =>
  email.from?.[0]?.name || email.from?.[0]?.email || "Unknown sender";

const getEmailPreview = (email: Email) =>
  (email.snippet || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const ProfileEmailsWidget: FC<Props> = ({ contactEmail, route }) => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.appReducer);
  const orgId = getLinkedFirm()?.orgId || "";

  const [connectEmail, { data: redirectUrl, isSuccess }] =
    useGetRedirectUrlMutation();
  const [
    getContactEmails,
    { data: contactEmails, error, isFetching: isLoadingEmails },
  ] = useLazyGetContactEmailsQuery();

  const {
    data: folders,
    isLoading: isLoadingFolders,
    error: foldersError,
  } = useGetEmailFoldersQuery(
    {
      userId: user?.id || "",
    },
    { skip: !user?.id },
  );

  const inboxFolder = useMemo(
    () => folders?.find((folder) => folder.name.toUpperCase() === "INBOX"),
    [folders],
  );

  const isConnected =
    error !== "Grant for this User doesn't exists. Try authorizing again." &&
    foldersError !==
      "Grant for this User doesn't exists. Try authorizing again.";

  useEffect(() => {
    if (!user?.id || !contactEmail || !orgId || !inboxFolder || !isConnected) {
      return;
    }

    getContactEmails({
      userId: user.id,
      pageNumber: undefined,
      folder: inboxFolder.name.toUpperCase(),
      contactEmail,
      orgId,
    });
  }, [
    contactEmail,
    getContactEmails,
    inboxFolder,
    isConnected,
    orgId,
    user?.id,
  ]);

  useEffect(() => {
    if (isSuccess && redirectUrl) {
      const link = document.createElement("a");

      link.setAttribute("href", redirectUrl);
      link.setAttribute("target", "_blank");
      link.click();
    }
  }, [isSuccess, redirectUrl]);

  const emails = contactEmails?.data?.slice(0, 4) || [];

  return (
    <Card extraClasses={"min-w-0 overflow-hidden"}>
      <div className={"flex h-[386px] min-w-0 max-w-full flex-col p-[22px]"}>
        <div
          className={"mb-4 flex shrink-0 items-center justify-between gap-4"}>
          <div className={"text-[19px] font-extrabold text-[#1F2433]"}>
            Emails
          </div>
          {!!emails.length && (
            <Button
              type={"button"}
              variant={"ghost"}
              size={"sm"}
              autoWidth
              rightIcon={<LuChevronRight size={14} strokeWidth={2.2} />}
              className={
                "h-auto p-0 text-[13px] font-bold text-[#6B4EFF] hover:bg-transparent"
              }
              onClick={() => navigate(route)}>
              View all
            </Button>
          )}
        </div>

        <div className={"flex min-h-0 flex-1 flex-col"}>
          {isLoadingFolders || isLoadingEmails ? (
            <div className={"p-4"}>
              <LottieLoading />
            </div>
          ) : !contactEmail ? (
            <ProfileWidgetEmptyState
              icon={<LuMail size={28} strokeWidth={1.7} />}
              title={"No email address"}
              description={
                "Add an email address to this profile to show messages."
              }
            />
          ) : !isConnected ? (
            <ProfileWidgetEmptyState
              icon={<LuMail size={28} strokeWidth={1.7} />}
              title={"Connect your email"}
              description={
                "Connect your email account to see incoming and outgoing messages here."
              }
              action={
                <Button
                  type={"button"}
                  variant={"primary"}
                  size={"sm"}
                  autoWidth
                  leftIcon={<LuPlus size={15} />}
                  className={"h-9 rounded-[9px] px-4 text-[13px] font-bold"}
                  onClick={() =>
                    connectEmail({
                      provider: "google",
                      returnUrl: document.URL,
                      userId: user?.id || "",
                    })
                  }>
                  Connect email
                </Button>
              }
            />
          ) : !emails.length ? (
            <ProfileWidgetEmptyState
              icon={<LuMail size={28} strokeWidth={1.7} />}
              title={"No emails yet"}
              description={
                "No emails with this client yet. New messages will appear here automatically."
              }
            />
          ) : (
            <div
              className={
                "ml-[-4px] mr-[-22px] min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden pl-1 pr-[22px]"
              }>
              {emails.map((email) => {
                const senderName = getSenderName(email);
                const hasAttachments = !!email.attachments?.length;
                const preview = getEmailPreview(email);

                return (
                  <div
                    key={email.id}
                    className={
                      "flex min-w-0 max-w-full cursor-pointer gap-[11px] overflow-hidden rounded-lg border-b border-[#F4F5F7] px-1.5 py-3 transition hover:bg-[#FAFAFC]"
                    }
                    onClick={() => navigate(route)}>
                    <Avatar
                      id={email.id}
                      fullName={senderName}
                      color={"primary"}
                      size={"sm"}
                      avatarClassNames={
                        "h-[26px] w-[26px] shrink-0 text-[10.5px] font-bold"
                      }
                    />
                    <div className={"min-w-0 flex-1"}>
                      <div
                        className={
                          "flex min-w-0 items-center justify-between gap-2 text-[13px]"
                        }>
                        <span
                          className={
                            "min-w-0 truncate font-bold text-[#3A4256]"
                          }>
                          {senderName}
                        </span>
                        <span
                          className={
                            "shrink-0 whitespace-nowrap text-xs text-[#9097A8]"
                          }>
                          {email.date
                            ? moment.unix(email.date).format("MMM DD")
                            : ""}
                        </span>
                      </div>
                      <div
                        className={
                          "my-0.5 min-w-0 truncate text-[13.5px] font-bold text-[#1F2433]"
                        }>
                        {email.subject || "No subject"}
                      </div>
                      {(hasAttachments || preview) && (
                        <div
                          className={
                            "flex min-w-0 max-w-full items-center gap-1.5 overflow-hidden text-[12.5px] text-[#9097A8]"
                          }>
                          {hasAttachments && (
                            <LuPaperclip
                              size={14}
                              className={"shrink-0 text-[#B6BCC8]"}
                            />
                          )}
                          {!!preview && (
                            <span
                              className={
                                "block min-w-0 max-w-full truncate [overflow-wrap:anywhere]"
                              }>
                              {preview}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
